import { oc } from "@orpc/contract";
import { z } from "zod";

const Outbound855MessageSchema = z.object({
  id: z.string(),
  transactionId: z.string().nullable(),
  error: z.boolean(),
  status: z.string().nullable(),
  createdAt: z.coerce.date(),
  completedAt: z.coerce.date().nullable(),
  poNumber: z.string().nullable(),
  receiverIsaId: z.string().nullable(),
  senderIsaId: z.string().nullable(),
  /** The SalesOrder.internalId extracted from REF*VN in the 855 payload */
  salesOrderId: z.number().int().nullable(),
});

export type Outbound855MessageDTO = z.infer<typeof Outbound855MessageSchema>;

const ListOutbound855MessagesInputSchema = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
});

// ---------------------------------------------------------------------------
// Shared schemas
// ---------------------------------------------------------------------------

const PageInfoSchema = z.object({
  currentPage: z.number().int(),
  totalPages: z.number().int(),
  totalCount: z.number().int(),
  hasNextPage: z.boolean(),
  hasPreviousPage: z.boolean(),
});

const ListOutbound855MessagesOutputSchema = z.object({
  edges: z.array(Outbound855MessageSchema),
  pageInfo: PageInfoSchema,
});

export type ListOutbound855MessagesOutput = z.infer<
  typeof ListOutbound855MessagesOutputSchema
>;

const Resend855InputSchema = z.object({
  salesOrderId: z.number().int().positive(),
});

const Resend855OutputSchema = z.object({
  /** true = 855 was sent, false = 855 was skipped (disabled for customer) */
  sent: z.boolean(),
});

// ---------------------------------------------------------------------------
// Famous Sales Orders (EDI Invoicing)
// ---------------------------------------------------------------------------

const OrderFetchStatusSchema = z.enum([
  "pending",
  "fetching",
  "success",
  "failed",
]);

const EdiInvoiceTypeSchema = z.enum(["810", "880"]);

const FamousSalesOrderSchema = z.object({
  kind: z.literal("famous_sales_order"),
  id: z.string().uuid(),
  famousSalesOrderId: z.string(),
  orderDetails: z.unknown().nullable(),
  orderFetchStatus: OrderFetchStatusSchema,
  orderFetchError: z.record(z.string(), z.unknown()).nullable(),
  edi810SentAt: z.coerce.date().nullable(),
  edi810Status: z.string().nullable(),
  edi810StatusLastFetchedAt: z.coerce.date().nullable(),
  ediInvoiceSentAt: z.coerce.date().nullable(),
  ediInvoiceStatus: z.string().nullable(),
  ediInvoiceTransactionId: z.string().nullable(),
  ediInvoiceValidationStatus: z.string().nullable(),
  ediInvoiceDeliveryStatus: z.string().nullable(),
  ediInvoiceStatusLastFetchedAt: z.coerce.date().nullable(),
  ediInvoiceType: EdiInvoiceTypeSchema.nullable(),
  customerId: z.string().uuid().nullable(),
  customerName: z.string().nullable(),
  salesOrderId: z.string().uuid().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type FamousSalesOrderDTO = z.infer<typeof FamousSalesOrderSchema>;

export const EdiSendStatusSchema = z.enum(["sent", "error", "not_sent"]);
export type EdiSendStatus = z.infer<typeof EdiSendStatusSchema>;

const ListFamousSalesOrdersInputSchema = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
  orderFetchStatus: OrderFetchStatusSchema.optional(),
  famousSalesOrderId: z.string().optional(),
  purchaseOrderNumber: z.string().optional(),
  id: z.string().uuid().optional(),
  customerId: z.string().uuid().optional(),
  ediInvoiceType: EdiInvoiceTypeSchema.optional(),
  ediSendStatus: EdiSendStatusSchema.optional(),
  importedFrom: z.coerce.date().optional(),
  importedTo: z.coerce.date().optional(),
});

export type ListFamousSalesOrdersInput = z.infer<
  typeof ListFamousSalesOrdersInputSchema
>;

const ListFamousSalesOrdersOutputSchema = z.object({
  edges: z.array(FamousSalesOrderSchema),
  pageInfo: PageInfoSchema,
});

export type ListFamousSalesOrdersOutput = z.infer<
  typeof ListFamousSalesOrdersOutputSchema
>;

const RefreshFamousSalesOrderInputSchema = z.object({
  famousSalesOrderId: z.string().uuid(),
  // Admin-only. When true, bypasses the "already sent" lock and clears the
  // sent-tracking fields so the invoice can be re-sent after refresh.
  force: z.boolean().optional(),
});

const RefreshFamousSalesOrderOutputSchema = FamousSalesOrderSchema;

export type RefreshFamousSalesOrderInput = z.infer<
  typeof RefreshFamousSalesOrderInputSchema
>;

export type RefreshFamousSalesOrderOutput = z.infer<
  typeof RefreshFamousSalesOrderOutputSchema
>;

// ---------------------------------------------------------------------------
// Orderful Config CRUD
// ---------------------------------------------------------------------------

const EdiConfigKeySchema = z.enum(["850", "855", "810", "856", "880", "875"]);

export type EdiConfigKey = z.infer<typeof EdiConfigKeySchema>;

export const UpsertOrderfulConfigInputSchema = z.object({
  customerId: z.string().uuid(),
  key: EdiConfigKeySchema,
  value: z.record(z.string(), z.unknown()),
});

export type UpsertOrderfulConfigInput = z.infer<
  typeof UpsertOrderfulConfigInputSchema
>;

const UpsertOrderfulConfigOutputSchema = z.object({
  id: z.string().uuid(),
  key: EdiConfigKeySchema,
  customerId: z.string().uuid(),
  value: z.record(z.string(), z.unknown()),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type UpsertOrderfulConfigOutput = z.infer<
  typeof UpsertOrderfulConfigOutputSchema
>;

export const DeleteOrderfulConfigInputSchema = z.object({
  customerId: z.string().uuid(),
  key: EdiConfigKeySchema,
});

export type DeleteOrderfulConfigInput = z.infer<
  typeof DeleteOrderfulConfigInputSchema
>;

const DeleteOrderfulConfigOutputSchema = z.object({
  success: z.boolean(),
});

export type DeleteOrderfulConfigOutput = z.infer<
  typeof DeleteOrderfulConfigOutputSchema
>;

// ---------------------------------------------------------------------------
// Partner presets — bulk-apply known-good config bundles for a trading partner
// ---------------------------------------------------------------------------

export const PARTNER_PRESET_IDS = ["procurant"] as const;
export const PartnerPresetIdSchema = z.enum(PARTNER_PRESET_IDS);
export type PartnerPresetId = z.infer<typeof PartnerPresetIdSchema>;

export const ApplyPartnerPresetInputSchema = z.object({
  customerId: z.string().uuid(),
  presetId: PartnerPresetIdSchema,
});

export type ApplyPartnerPresetInput = z.infer<
  typeof ApplyPartnerPresetInputSchema
>;

const ApplyPartnerPresetOutputSchema = z.object({
  /** EDI keys that had their config upserted */
  applied: z.array(EdiConfigKeySchema),
});

export type ApplyPartnerPresetOutput = z.infer<
  typeof ApplyPartnerPresetOutputSchema
>;

// ---------------------------------------------------------------------------
// Famous Order Confirmations (EDI 855)
// ---------------------------------------------------------------------------

const FamousOrderConfirmationSchema = z.object({
  id: z.string().uuid(),
  famousOrderId: z.string(),
  orderDetails: z.unknown().nullable(),
  orderFetchStatus: OrderFetchStatusSchema,
  orderFetchError: z.record(z.string(), z.unknown()).nullable(),
  edi855SentAt: z.coerce.date().nullable(),
  edi855Status: z.string().nullable(),
  edi855TransactionId: z.string().nullable(),
  edi855Error: z.string().nullable(),
  edi855StatusLastFetchedAt: z.coerce.date().nullable(),
  customerId: z.string().uuid().nullable(),
  customerName: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type FamousOrderConfirmationDTO = z.infer<
  typeof FamousOrderConfirmationSchema
>;

const ListOrderConfirmationsInputSchema = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
  famousOrderId: z.string().optional(),
  customerId: z.string().uuid().optional(),
  edi855Status: z.enum(["not_sent", "pending", "sent", "error"]).optional(),
});

const ListOrderConfirmationsOutputSchema = z.object({
  edges: z.array(FamousOrderConfirmationSchema),
  pageInfo: PageInfoSchema,
});

export type ListOrderConfirmationsOutput = z.infer<
  typeof ListOrderConfirmationsOutputSchema
>;

const FetchOrderConfirmationInputSchema = z.object({
  famousOrderId: z.string(),
});

const FetchOrderConfirmationOutputSchema = FamousOrderConfirmationSchema;

const GetOrderConfirmationInputSchema = z.object({
  orderConfirmationId: z.string().uuid(),
});

const GetOrderConfirmationOutputSchema = FamousOrderConfirmationSchema;

const SyncOrderConfirmationInputSchema = z.object({
  orderConfirmationId: z.string().uuid(),
  // Admin-only. When true, bypasses the "already sent" lock and clears the
  // 855 sent-tracking fields so the confirmation can be re-sent after refresh.
  force: z.boolean().optional(),
});

const SyncOrderConfirmationOutputSchema = FamousOrderConfirmationSchema;

const DeleteOrderConfirmationInputSchema = z.object({
  orderConfirmationId: z.string().uuid(),
});

const DeleteOrderConfirmationOutputSchema = z.object({
  success: z.boolean(),
});

const SendOrderConfirmation855InputSchema = z.object({
  orderConfirmationId: z.string().uuid(),
  force: z.boolean().optional(),
});

const SendOrderConfirmation855OutputSchema = z.object({
  sent: z.boolean(),
});

export const ediContracts = {
  outbound855Messages: {
    list: oc
      .input(ListOutbound855MessagesInputSchema)
      .output(ListOutbound855MessagesOutputSchema)
      .route({ method: "GET" }),

    resend: oc
      .input(Resend855InputSchema)
      .output(Resend855OutputSchema)
      .route({ method: "POST" }),
  },

  famousSalesOrders: {
    list: oc
      .input(ListFamousSalesOrdersInputSchema)
      .output(ListFamousSalesOrdersOutputSchema)
      .route({ method: "GET" }),

    refresh: oc
      .input(RefreshFamousSalesOrderInputSchema)
      .output(RefreshFamousSalesOrderOutputSchema)
      .route({ method: "POST" }),
  },

  orderConfirmations: {
    list: oc
      .input(ListOrderConfirmationsInputSchema)
      .output(ListOrderConfirmationsOutputSchema)
      .route({ method: "GET" }),

    fetch: oc
      .input(FetchOrderConfirmationInputSchema)
      .output(FetchOrderConfirmationOutputSchema)
      .route({ method: "POST" }),

    get: oc
      .input(GetOrderConfirmationInputSchema)
      .output(GetOrderConfirmationOutputSchema)
      .route({ method: "GET" }),

    sync: oc
      .input(SyncOrderConfirmationInputSchema)
      .output(SyncOrderConfirmationOutputSchema)
      .route({ method: "POST" }),

    delete: oc
      .input(DeleteOrderConfirmationInputSchema)
      .output(DeleteOrderConfirmationOutputSchema)
      .route({ method: "POST" }),

    sendEdi855: oc
      .input(SendOrderConfirmation855InputSchema)
      .output(SendOrderConfirmation855OutputSchema)
      .route({ method: "POST" }),
  },

  orderfulConfigs: {
    upsert: oc
      .input(UpsertOrderfulConfigInputSchema)
      .output(UpsertOrderfulConfigOutputSchema)
      .route({ method: "POST" }),

    delete: oc
      .input(DeleteOrderfulConfigInputSchema)
      .output(DeleteOrderfulConfigOutputSchema)
      .route({ method: "POST" }),

    applyPreset: oc
      .input(ApplyPartnerPresetInputSchema)
      .output(ApplyPartnerPresetOutputSchema)
      .route({ method: "POST" }),
  },
} as const;

export type EdiContracts = typeof ediContracts;
